import type { IconInput } from '../icon/icon';

/** A single sidebar link that navigates. */
export interface NavLink {
  /**
   * Stable identity, used for rendering and state tracking. Optional: a link
   * falls back to its `route`. Set it when the fallback isn't unique.
   */
  id?: string;
  /** Text shown next to the icon; also the accessible name when the rail is collapsed. */
  label: string;
  /** Icon name or semantic alias — see `ICON_ALIASES` in `@sisoma/ui`. */
  icon: IconInput;
  /**
   * Root-relative router path, e.g. `'dashboard'` or `'schedule/review'` — a
   * leading slash is optional and has no effect.
   */
  route: string;
  /** Only mark active on an exact URL match (use for index routes). */
  exact?: boolean;
  /** Optional counter chip rendered after the label, e.g. pending approvals. */
  badge?: string;
}

/**
 * A collapsible group. The parent does not navigate — clicking it expands the
 * child links in place. The group containing the active route opens
 * automatically, so deep links land expanded.
 */
export interface NavGroup {
  /**
   * Stable identity, used for rendering and expansion state. Optional: a
   * group falls back to its `label` — set an `id` when two groups may share
   * a label, or when labels are user-facing text that can change (renames,
   * translation) without the group being a different thing.
   */
  id?: string;
  label: string;
  icon: IconInput;
  /** Child links, revealed when the group is expanded. */
  children: NavLink[];
}

/**
 * A sidebar entry: either a link or a collapsible group of links.
 *
 * The two are told apart by which required property they carry (`route` vs
 * `children`) rather than by optional `never` markers, because the latter only
 * discriminate under `strictNullChecks`. Use `isNavGroup()` to narrow.
 */
export type NavItem = NavLink | NavGroup;

/** Type guard: true when the entry expands rather than navigates. */
export function isNavGroup(item: NavItem): item is NavGroup {
  return 'children' in item;
}

/**
 * The stable identity of an entry: explicit `id`, else a link's `route`,
 * else the label. Display text is the last resort — it collides across
 * same-labelled entries and changes under rename/translation.
 */
export function navItemKey(item: NavItem): string {
  if (item.id) {
    return item.id;
  }
  return isNavGroup(item) ? item.label : item.route;
}

/** A titled group of sidebar entries. */
export interface NavSection {
  /** Optional group heading; hidden while the sidenav is collapsed. */
  heading?: string;
  items: NavItem[];
}
